import React, { createContext, useEffect, useReducer } from 'react';
import { useHistory } from 'react-router';
import { useQueryParams } from 'hooks';
import { OrderDirection, OrderFilter } from 'types';
import { formatDateAsQueryParam } from 'utils/date';

interface Props {
  children: any;
}

export const DEFAULT_ITEMS_PER_PAGE = 25;
export const DEFAULT_ITEMS_PER_SELECT = 500;

export interface QueryParamFilters {
  page: number;
  itemsPerPage: number;
  search: string;
  includeCancelled: boolean;
  orderBy: string | null;
  order: OrderDirection;
}

type Action =
  | { type: 'reset_filters' }
  | { type: 'update_search'; payload: string }
  | { type: 'set_page'; payload: number }
  | { type: 'set_itemsPerPage'; payload: number }
  | { type: 'set_include_cancelled'; payload: boolean }
  | { type: 'set_search'; payload: string }
  | { type: 'update_order'; payload: OrderFilter };

export interface TableFilterContextInterface extends QueryParamFilters {
  dispatch: (action: Action) => void;
}

const defaultState: QueryParamFilters = {
  page: 1,
  itemsPerPage: DEFAULT_ITEMS_PER_PAGE,
  search: '',
  includeCancelled: false,
  orderBy: null,
  order: 'asc',
};

export const TableFilterContext = createContext<TableFilterContextInterface>({
  ...defaultState,
  dispatch: () => {},
});

const queryParamsReducer: React.Reducer<QueryParamFilters, Action> = (state: QueryParamFilters, action: Action) => {
  switch (action.type) {
    case 'reset_filters':
      return defaultState;
    case 'update_search':
      return { ...state, search: action.payload };
    case 'set_page':
      return { ...state, page: action.payload };
    case 'set_itemsPerPage':
      return { ...state, itemsPerPage: action.payload, page: 1 };
    case 'set_include_cancelled':
      return { ...state, includeCancelled: action.payload, page: 1 };
    case 'set_search':
      return { ...state, search: action.payload, page: 1, itemsPerPage: defaultState.itemsPerPage };
    case 'update_order':
      return { ...state, ...action.payload, page: 1 };
    default:
      throw new Error();
  }
};

const prepareQueryParams = (state: QueryParamFilters): { [key: string]: any } => {
  const { includeCancelled, ...rest } = state;

  return {
    ...rest,
    include_cancelled: state.includeCancelled,
  };
};

const stateToQueryParams = (state: QueryParamFilters): URLSearchParams => {
  const query = new URLSearchParams();

  const preparedQueryParams = prepareQueryParams(state);
  const defaultQueryParams = prepareQueryParams(defaultState);

  for (let k in preparedQueryParams) {
    const requestedValue: any = preparedQueryParams[k];
    const defaultValue: any = defaultQueryParams[k];

    if (!requestedValue || requestedValue === defaultValue) {
      // No need to add empty or default query params
      // we will fallback on default anyways
      continue;
    }

    if (requestedValue instanceof Date) {
      query.set(k, formatDateAsQueryParam(requestedValue));
    } else {
      query.set(k, requestedValue.toString());
    }
  }

  return query;
};

const queryParamsToState = (queryParams: URLSearchParams): QueryParamFilters => {
  return {
    page: Number(queryParams.get('page') ?? defaultState['page']),
    itemsPerPage: Number(queryParams.get('itemsPerPage') ?? defaultState['itemsPerPage']),
    search: queryParams.get('search') ?? defaultState['search'],
    includeCancelled: queryParams.get('include_cancelled') === 'true',
    order: (queryParams.get('order') as OrderDirection) ?? defaultState['order'],
    orderBy: queryParams.get('orderBy') ?? defaultState['orderBy'],
  };
};

export const TableFilterProvider: React.FC<Props> = ({ children }) => {
  const queryParams = useQueryParams();

  const [state, dispatch] = useReducer(queryParamsReducer, queryParamsToState(queryParams));
  const history = useHistory();
  const previousQueryParams = queryParams.toString();
  const query = stateToQueryParams(state);
  const newQueryParams = query.toString();

  useEffect(() => {
    if (newQueryParams !== previousQueryParams) {
      history.push({ search: newQueryParams });
    }
  }, [history, newQueryParams, previousQueryParams]);

  return <TableFilterContext.Provider value={{ ...state, dispatch }}>{children}</TableFilterContext.Provider>;
};
